/**
 * Layout list component.
 *
 * In preview mode, repeats the configured row template for each item resolved
 * from the list binding (up to maxItems). The template is rendered using the
 * same registry-driven renderers as other layout nodes.
 *
 * Supports enter/exit transitions via Behavior Blueprint events
 * (`onListEnter` / `onListExit`) and smooth relayout via configurable CSS
 * transitions (move / variant height changes).
 */

import { Box } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ComponentNode,
  ContainerNode,
  DataBinding,
  InjectedData,
  LayoutListComponentProps,
  LayoutNode,
} from "../layout-types";
import { resolveBinding, resolvePath, resolveStyles } from "../layout-types";
import {
  DEFAULT_LAYOUT_LIST_DIRECTION,
  DEFAULT_LAYOUT_LIST_MAX_ITEMS,
  isLayoutListDirectionHorizontal,
  resolveLayoutListRowOffsets,
  type LayoutListDirection,
} from "./layoutListConfig";
import { extractSceneAnimations } from "../../../../util/src/blueprint/runtime/scene-animations.js";
import {
  estimateListExitWaitMsFromBehavior,
  hasOnListExitConnectedToAnimPlay,
} from "../../../../util/src/blueprint/runtime/helpers.js";
import { useBehavior } from "../useBehavior.js";
import { mergeBehaviorComponent } from "../resolveBehaviorStyles.js";
import { getDataBindingSource } from "../../../../util/src/layout/types";
import {
  areRowBindingFingerprintsEqual,
  buildLayoutListScopedRowData,
  collectLayoutListRowBindingExpressions,
  collectRowTemplateUsedExternalSources,
  computeRowBindingFingerprint,
  hasInjectableSourcesChanged,
  serializeRowBindingFingerprint,
  type RowBindingFingerprint,
} from "../../../../util/src/layout/layout-row-binding-fingerprint.js";
import type { BindingExpression } from "../../../../util/src/blueprint/types.js";
import {
  buildCssTransition,
  buildNodeClipPath,
  parseRotationDegrees,
} from "../LayoutRenderer";

// ---------------------------------------------------------------------------
// Transition engine types & helpers
// ---------------------------------------------------------------------------

/**
 * Derive a stable key from an item using a dot-separated path.
 * Falls back to stringified index when the path yields nothing useful.
 */
function deriveStableKey(
  item: unknown,
  rowKeyPath: string | undefined,
  index: number,
): string {
  if (!rowKeyPath) return String(index);
  const value = resolvePath(item, rowKeyPath);
  if (value == null) return String(index);
  return String(value);
}

export interface LayoutListClipRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface LayoutListProps {
  nodeRef: React.RefObject<HTMLElement | null>;
  node: ComponentNode;
  data: InjectedData;
  scale: number;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  sceneCatalog?: import("../layout-types").SceneBehaviorCatalog;
  previewTickByNodeId?: Record<string, number>;
  isEditor: boolean;
  /** Disable behavior graph execution while keeping the static list render intact. */
  disableBehaviors?: boolean;
  inFlow?: boolean;
  renderedX: number;
  renderedY: number;
  renderedZIndex: number;
  resolvedStyle: Record<string, unknown>;
  clipRect?: LayoutListClipRect;
  renderNode: (params: {
    node: LayoutNode;
    data: InjectedData;
    parentOffsetX?: number;
    parentOffsetY?: number;
    clipRect?: LayoutListClipRect;
    isFlex: boolean;
    transition?: string;
    disableAnimatedClipTracking?: boolean;
  }) => React.ReactElement;
}

type LayoutListFilterPredicate = (
  dataBinding: DataBinding,
  rowIndex: number,
  rowsOverride?: unknown[],
) => boolean;
type LayoutListSortComparator = (
  dataBinding: DataBinding,
  aIndex: number,
  bIndex: number,
  latestInjectedData: InjectedData,
  rowsOverride?: unknown[],
) => number;
type LayoutListRowVariantRuntime = {
  name: string;
  order?: number;
  conditionEvaluator: (dataBinding: DataBinding, data: InjectedData) => boolean;
};

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const toScaledCssLength = (
  value: unknown,
  scale: number,
): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value) * scale}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const trimmed = value.trim();
    if (trimmed.endsWith("px")) {
      const parsed = Number.parseFloat(trimmed.replace("px", "").trim());
      if (Number.isFinite(parsed)) {
        return `${Math.max(0, parsed) * scale}px`;
      }
    }
    return trimmed;
  }
  return undefined;
};

const getResolvedBorderSx = (
  resolvedStyle: Record<string, unknown>,
): Record<string, unknown> => ({
  border: resolvedStyle.border as string | undefined,
  borderTop: resolvedStyle.borderTop as string | undefined,
  borderRight: resolvedStyle.borderRight as string | undefined,
  borderBottom: resolvedStyle.borderBottom as string | undefined,
  borderLeft: resolvedStyle.borderLeft as string | undefined,
  borderRadius: toCssLength(resolvedStyle.borderRadius),
  borderTopLeftRadius: toCssLength(resolvedStyle.borderTopLeftRadius),
  borderTopRightRadius: toCssLength(resolvedStyle.borderTopRightRadius),
  borderBottomRightRadius: toCssLength(resolvedStyle.borderBottomRightRadius),
  borderBottomLeftRadius: toCssLength(resolvedStyle.borderBottomLeftRadius),
});

const getResolvedSpacingSx = (
  resolvedStyle: Record<string, unknown>,
  scale: number,
  options?: { includeMargin?: boolean },
): Record<string, unknown> => {
  const includeMargin = options?.includeMargin ?? true;
  return {
    margin: includeMargin
      ? toScaledCssLength(resolvedStyle.margin, scale)
      : undefined,
    marginTop: includeMargin
      ? toScaledCssLength(resolvedStyle.marginTop, scale)
      : undefined,
    marginRight: includeMargin
      ? toScaledCssLength(resolvedStyle.marginRight, scale)
      : undefined,
    marginBottom: includeMargin
      ? toScaledCssLength(resolvedStyle.marginBottom, scale)
      : undefined,
    marginLeft: includeMargin
      ? toScaledCssLength(resolvedStyle.marginLeft, scale)
      : undefined,
    padding: toScaledCssLength(resolvedStyle.padding, scale),
    paddingTop: toScaledCssLength(resolvedStyle.paddingTop, scale),
    paddingRight: toScaledCssLength(resolvedStyle.paddingRight, scale),
    paddingBottom: toScaledCssLength(resolvedStyle.paddingBottom, scale),
    paddingLeft: toScaledCssLength(resolvedStyle.paddingLeft, scale),
  };
};

const composeNodeTransform = (
  baseTransform: string | undefined,
  rotation: number | undefined,
): string | undefined => {
  const normalizedTransform =
    typeof baseTransform === "string" && baseTransform.trim().length > 0
      ? baseTransform.trim()
      : undefined;
  const normalizedRotation =
    typeof rotation === "number" && Number.isFinite(rotation) ? rotation : 0;

  if (!normalizedTransform && normalizedRotation === 0) return undefined;
  if (!normalizedTransform) return `rotate(${normalizedRotation}deg)`;
  if (normalizedRotation === 0) return normalizedTransform;
  return `${normalizedTransform} rotate(${normalizedRotation}deg)`;
};

const intersectClipRects = (
  baseClip: LayoutListClipRect | undefined,
  nextClip: LayoutListClipRect,
): LayoutListClipRect => {
  if (!baseClip) return nextClip;
  return {
    left: Math.max(baseClip.left, nextClip.left),
    top: Math.max(baseClip.top, nextClip.top),
    right: Math.min(baseClip.right, nextClip.right),
    bottom: Math.min(baseClip.bottom, nextClip.bottom),
  };
};

const buildLayoutListRenderedClipPath = (
  clipRect: LayoutListClipRect | undefined,
  canvasNodeLeft: number,
  canvasNodeTop: number,
  contentWidth: number,
  contentHeight: number,
  scale: number,
  node: ComponentNode,
): string | undefined => {
  if (!clipRect) return undefined;
  return buildNodeClipPath(
    clipRect,
    canvasNodeLeft,
    canvasNodeTop,
    contentWidth,
    contentHeight,
    scale,
    parseRotationDegrees(
      composeNodeTransform(node.style?.transform, node.rotation),
    ),
  );
};

const SKEW_X_REGEX = /skewX\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/i;
const SKEW_Y_REGEX = /skewY\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/i;

const parseSkewAxis = (
  transform: string | undefined,
  axis: "x" | "y",
): number => {
  if (!transform) return 0;
  const match = (axis === "x" ? SKEW_X_REGEX : SKEW_Y_REGEX).exec(transform);
  const value = Number(match?.[1] ?? 0);
  if (!Number.isFinite(value)) return 0;
  return Math.round(value);
};

const areRowsEquivalentByKey = (
  left: Array<{ item: unknown; originalIndex: number }>,
  right: Array<{ item: unknown; originalIndex: number }>,
  rowKeyPath: string | undefined,
): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const leftEntry = left[index];
    const rightEntry = right[index];
    if (!leftEntry || !rightEntry) return false;
    const leftKey = deriveStableKey(
      leftEntry.item,
      rowKeyPath,
      leftEntry.originalIndex,
    );
    const rightKey = deriveStableKey(
      rightEntry.item,
      rowKeyPath,
      rightEntry.originalIndex,
    );
    if (leftKey !== rightKey) return false;
    if (!Object.is(leftEntry.item, rightEntry.item)) {
      return false;
    }
  }
  return true;
};
export const LayoutList: React.FC<LayoutListProps> = ({
  nodeRef,
  node,
  data,
  scale,
  sceneCatalog,
  renderedX,
  renderedY,
  renderedZIndex,
  resolvedStyle,
  clipRect,
  renderNode,
  isEditor,
  disableBehaviors = false,
  inFlow = false,
}) => {
  const nodeTransform =
    typeof node.style?.transform === "string"
      ? node.style.transform
      : undefined;
  const currentNodeContext = React.useMemo(
    () => ({
      x: Math.round(renderedX),
      y: Math.round(renderedY),
      width: Math.round(node.width),
      height: Math.round(node.height),
      rotation: Math.round(node.rotation ?? 0),
      skewX: parseSkewAxis(nodeTransform, "x"),
      skewY: parseSkewAxis(nodeTransform, "y"),
    }),
    [
      node.height,
      node.rotation,
      node.width,
      nodeTransform,
      renderedX,
      renderedY,
    ],
  );
  const sceneAnimationsByName = React.useMemo(
    () =>
      disableBehaviors
        ? {}
        : extractSceneAnimations(sceneCatalog, data, currentNodeContext).byName,
    [currentNodeContext, data, disableBehaviors, sceneCatalog],
  );
  const { componentPatch, componentBindingPatch, hasRender } = useBehavior(
    disableBehaviors ? undefined : node.behavior,
    data,
    currentNodeContext,
    sceneAnimationsByName,
  );

  const resolvedComponentPatch = React.useMemo(
    () =>
      mergeBehaviorComponent(
        componentPatch,
        componentBindingPatch,
        data,
        node.dataBinding,
      ),
    [componentBindingPatch, componentPatch, data, node.dataBinding],
  );

  const nodeWithBehaviorProps = React.useMemo(() => {
    if (!resolvedComponentPatch || node.kind !== "component") return node;
    return {
      ...node,
      props: {
        ...(node.props ?? {}),
        ...resolvedComponentPatch,
      },
    } satisfies ComponentNode;
  }, [resolvedComponentPatch, node]);

  const rowItem = useMemo(() => {
    return nodeWithBehaviorProps.dataBinding
      ? resolveBinding(
          { path: nodeWithBehaviorProps.dataBinding.path + ".0" },
          data,
        )
      : undefined;
  }, [nodeWithBehaviorProps.dataBinding, data]);

  const editRowData = useMemo(
    (): InjectedData => ({
      ...data,
      rowIndex: 0,
      layoutListRowItem: rowItem,
    }),
    [data, rowItem],
  );

  if (!hasRender) return null;
  const listProps = (nodeWithBehaviorProps.props ??
    {}) as Partial<LayoutListComponentProps>;
  if (!listProps.rowTemplate) return null;
  const rowTemplate = listProps.rowTemplate;
  const editRowTemplate =
    listProps.mode === "edit" && listProps.editRowVariantName
      ? ((listProps.rowVariants ?? []).find(
          (variant) => variant.name === listProps.editRowVariantName,
        )?.template ?? rowTemplate)
      : rowTemplate;

  if ((listProps.mode ?? "preview") === "edit" && isEditor) {
    const editContentWidth =
      editRowTemplate.width +
      getMarginInsetsFromStyle(editRowTemplate.style ?? {}).horizontal +
      getMarginInsetsFromStyle(node.style ?? {}).horizontal;
    const editContentHeight =
      editRowTemplate.height +
      getMarginInsetsFromStyle(editRowTemplate.style ?? {}).vertical +
      getMarginInsetsFromStyle(node.style ?? {}).vertical;
    const editRenderedClipPath = inFlow
      ? undefined
      : buildLayoutListRenderedClipPath(
          clipRect,
          renderedX,
          renderedY,
          editContentWidth,
          editContentHeight,
          scale,
          node,
        );
    const rowX = renderedX + editRowTemplate.x;
    const rowY = renderedY + editRowTemplate.y;
    const rowClipRect = intersectClipRects(clipRect, {
      left: rowX,
      top: rowY,
      right: rowX + editRowTemplate.width,
      bottom: rowY + editRowTemplate.height,
    });

    return (
      <Box
        key={`${node.id}::layer`}
        sx={{
          position:
            node.style?.position === "relative" ? "relative" : "absolute",
          inset: 0,
          zIndex: renderedZIndex,
          pointerEvents: "none",
        }}
      >
        <Box
          key={`${node.id}`}
          ref={nodeRef}
          sx={{
            position:
              node.style?.position === "relative" ? "relative" : "absolute",
            left: renderedX * scale,
            top: renderedY * scale,
            width: editContentWidth * scale,
            height: editContentHeight * scale,
            zIndex: 0,
            transform: composeNodeTransform(
              node.style?.transform,
              node.rotation,
            ),
            transformOrigin: "center center",
            boxSizing: "border-box",
            clipPath: editRenderedClipPath,
            WebkitClipPath: editRenderedClipPath,
            ...getResolvedSpacingSx(resolvedStyle, scale, {
              includeMargin: false,
            }),
            pointerEvents: "none",
          }}
        >
          {" "}
          {editRowTemplate.layout === "flex" &&
            renderNode({
              node: editRowTemplate,
              data: editRowData,
              parentOffsetX: editRowTemplate.x,
              parentOffsetY: editRowTemplate.y,
              clipRect: rowClipRect,
              isFlex: editRowTemplate.layout === "flex",
            })}
        </Box>
        {editRowTemplate.layout === "absolute" &&
          renderNode({
            node: editRowTemplate,
            data: editRowData,
            parentOffsetX: rowX,
            parentOffsetY: rowY,
            clipRect: rowClipRect,
            isFlex: false,
          })}
      </Box>
    );
  }

  return (
    <LayoutListPreview
      node={node}
      nodeRef={nodeRef}
      nodeWithBehaviorProps={nodeWithBehaviorProps}
      listProps={listProps}
      rowTemplate={rowTemplate}
      data={data}
      scale={scale}
      renderedX={renderedX}
      renderedY={renderedY}
      renderedZIndex={renderedZIndex}
      resolvedStyle={resolvedStyle}
      inFlow={inFlow}
      clipRect={clipRect}
      renderNode={renderNode}
      disableBehaviors={disableBehaviors}
      sceneCatalog={sceneCatalog}
      componentBindingPatch={componentBindingPatch}
    />
  );
};

// ---------------------------------------------------------------------------
// Preview sub-component (with transition engine)
// ---------------------------------------------------------------------------

interface LayoutListPreviewProps {
  node: ComponentNode;
  nodeRef: React.RefObject<HTMLElement | null>;
  nodeWithBehaviorProps: ComponentNode;
  listProps: Partial<LayoutListComponentProps>;
  rowTemplate: ContainerNode;
  data: InjectedData;
  scale: number;
  renderedX: number;
  renderedY: number;
  renderedZIndex: number;
  resolvedStyle: Record<string, unknown>;
  inFlow: boolean;
  clipRect?: LayoutListClipRect;
  renderNode: LayoutListProps["renderNode"];
  disableBehaviors: boolean;
  sceneCatalog?: import("../layout-types").SceneBehaviorCatalog;
  componentBindingPatch?: import("../../../../util/src/blueprint/runtime/types.js").ComponentBindingPatch;
}

/**
 * Resolved row entry ready for rendering.
 */
interface ResolvedRow {
  key: string;
  originalIndex: number;
  visibleIndex: number;
  item: unknown;
  isExiting: boolean;
  isAdded: boolean;
  template: ContainerNode;
  offsetX: number;
  offsetY: number;
  outerHeight: number;
  outerWidth: number;
}

const buildLayoutListRowClipRect = (
  direction: LayoutListDirection,
  previewContentWidth: number,
  previewContentHeight: number,
  offsetX: number,
  offsetY: number,
): LayoutListClipRect => {
  if (isLayoutListDirectionHorizontal(direction)) {
    return {
      left: 0,
      top: 0,
      right: Math.max(0, previewContentWidth - offsetX),
      bottom: previewContentHeight,
    };
  }
  return {
    left: 0,
    top: 0,
    right: previewContentWidth,
    bottom: Math.max(0, previewContentHeight - offsetY),
  };
};

interface RowSnapshot {
  row: ResolvedRow;
  frozenData: InjectedData;
}

interface DisplayedRowEntry {
  item: unknown;
  originalIndex: number;
  isExiting?: boolean;
  isAdded?: boolean;
}

/**
 * Props for a single preview row shell (wrapper + `renderNode` subtree).
 */
interface LayoutListPreviewRowProps {
  row: ResolvedRow;
  rowData: InjectedData;
  rowTemplateNode: ContainerNode;
  scale: number;
  listDirection: LayoutListDirection;
  previewContentWidth: number;
  previewContentHeight: number;
  rowKeyPath: string | undefined;
  listSourceKey: string | undefined;
  bindingFingerprint?: RowBindingFingerprint;
  renderNode: LayoutListProps["renderNode"];
  registerRowElement: (key: string, element: HTMLDivElement | null) => void;
}

const clipRectsEqual = (
  left: LayoutListClipRect,
  right: LayoutListClipRect,
): boolean =>
  left.left === right.left &&
  left.top === right.top &&
  left.right === right.right &&
  left.bottom === right.bottom;

const resolveMatchedRowVariant = (
  variants: LayoutListRowVariantRuntime[],
  dataBinding: DataBinding | undefined,
  scopedRowData: InjectedData,
): LayoutListRowVariantRuntime | undefined => {
  if (!dataBinding) return undefined;
  return variants.find((variant) => {
    try {
      return Boolean(variant.conditionEvaluator(dataBinding, scopedRowData));
    } catch {
      return false;
    }
  });
};

const areLayoutListPreviewRowPropsEqual = (
  prev: LayoutListPreviewRowProps,
  next: LayoutListPreviewRowProps,
): boolean => {
  if (prev.scale !== next.scale) return false;
  if (prev.renderNode !== next.renderNode) return false;
  if (prev.registerRowElement !== next.registerRowElement) return false;

  const prevRow = prev.row;
  const nextRow = next.row;
  if (prevRow.key !== nextRow.key) return false;
  if (!Object.is(prevRow.item, nextRow.item)) return false;
  if (prevRow.offsetX !== nextRow.offsetX) return false;
  if (prevRow.offsetY !== nextRow.offsetY) return false;
  if (prev.listDirection !== next.listDirection) return false;
  if (prevRow.outerWidth !== nextRow.outerWidth) return false;
  if (prevRow.outerHeight !== nextRow.outerHeight) return false;
  if (prevRow.isExiting !== nextRow.isExiting) return false;
  if (prevRow.isAdded !== nextRow.isAdded) return false;
  if (prevRow.visibleIndex !== nextRow.visibleIndex) return false;
  if (prevRow.template !== nextRow.template) return false;

  if (prev.rowTemplateNode.id !== next.rowTemplateNode.id) return false;
  if (prev.rowTemplateNode.zIndex !== next.rowTemplateNode.zIndex) return false;

  if (!prev.row.isExiting && !next.row.isExiting) {
    if (
      !areRowBindingFingerprintsEqual(
        prev.bindingFingerprint,
        next.bindingFingerprint,
      )
    ) {
      return false;
    }
  }

  if (
    !clipRectsEqual(
      buildLayoutListRowClipRect(
        prev.listDirection,
        prev.previewContentWidth,
        prev.previewContentHeight,
        prevRow.offsetX,
        prevRow.offsetY,
      ),
      buildLayoutListRowClipRect(
        next.listDirection,
        next.previewContentWidth,
        next.previewContentHeight,
        nextRow.offsetX,
        nextRow.offsetY,
      ),
    )
  ) {
    return false;
  }

  if (prev.rowData.rowIndex !== next.rowData.rowIndex) return false;
  if (prev.rowData.listTransitionSignal !== next.rowData.listTransitionSignal) {
    return false;
  }
  if (
    !Object.is(prev.rowData.layoutListRowItem, next.rowData.layoutListRowItem)
  ) {
    return false;
  }
  if (!Object.is(prevRow.item, nextRow.item)) {
    return false;
  }

  return true;
};

const LayoutListPreviewRow = React.memo(function LayoutListPreviewRow({
  row,
  rowData,
  rowTemplateNode,
  scale,
  listDirection,
  previewContentWidth,
  previewContentHeight,
  renderNode,
  registerRowElement,
}: LayoutListPreviewRowProps) {
  const rowTransition = useMemo(
    () => buildCssTransition(row.template.transitions),
    [row.template.transitions],
  );

  const rowClipRect = useMemo(
    (): LayoutListClipRect =>
      buildLayoutListRowClipRect(
        listDirection,
        previewContentWidth,
        previewContentHeight,
        row.offsetX,
        row.offsetY,
      ),
    [
      listDirection,
      previewContentHeight,
      previewContentWidth,
      row.offsetX,
      row.offsetY,
    ],
  );

  const handleRowRef = useCallback(
    (element: HTMLDivElement | null) => {
      registerRowElement(row.key, element);
    },
    [registerRowElement, row.key],
  );

  return (
    <div
      ref={handleRowRef}
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: `${row.outerWidth * scale}px`,
        height: `${row.outerHeight * scale}px`,
        transform: `translate(${row.offsetX * scale}px, ${row.offsetY * scale}px)`,
        pointerEvents: "none",
        zIndex: 1,
        transition: rowTransition,
      }}
    >
      {renderNode({
        node: rowTemplateNode,
        data: rowData,
        parentOffsetX: 0,
        parentOffsetY: 0,
        clipRect: rowClipRect,
        isFlex: row.template.layout === "flex",
        disableAnimatedClipTracking: true,
      })}
    </div>
  );
}, areLayoutListPreviewRowPropsEqual);

type LayoutListRowTemplateVariant = {
  template: ContainerNode;
};

type LayoutListBehaviorGraph = NonNullable<LayoutNode["behavior"]>;

/**
 * Collects every behavior graph attached to a row template container subtree.
 *
 * @param node - Root layout node (typically a row template container).
 * @returns Behavior graphs found on the node and its container descendants.
 */
const collectLayoutNodeBehaviors = (
  node: LayoutNode,
): LayoutListBehaviorGraph[] => {
  const graphs: LayoutListBehaviorGraph[] = [];
  if (node.behavior?.nodes?.length) {
    graphs.push(node.behavior);
  }
  if (node.kind === "container") {
    for (const child of node.children) {
      graphs.push(...collectLayoutNodeBehaviors(child));
    }
  }
  return graphs;
};

/**
 * Whether row snapshots should reuse frozen injected data between frames.
 *
 * Frozen data is required when an exit animation can run: any row template
 * (default or variant) or one of its descendants wires `onListExit` to `animPlay`.
 *
 * @param rowTemplate - Default row template container.
 * @param rowVariants - Optional variant templates from layout list props.
 * @returns True when frozen snapshots must be preserved for exit transitions.
 */
const shouldUseFrozenData = (
  rowTemplate: ContainerNode,
  rowVariants?: ReadonlyArray<LayoutListRowTemplateVariant>,
): boolean => {
  const templates = [
    rowTemplate,
    ...(rowVariants ?? []).map((variant) => variant.template),
  ];
  return templates.some((template) =>
    collectLayoutNodeBehaviors(template).some((behavior) =>
      hasOnListExitConnectedToAnimPlay(behavior),
    ),
  );
};

const toNumberLength = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value.replace("px", "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const getMarginInsetsFromStyle = (
  style: Record<string, unknown>,
): {
  top: number;
  right: number;
  bottom: number;
  left: number;
  horizontal: number;
  vertical: number;
} => {
  const baseMargin = toNumberLength(style.margin);
  const top = toNumberLength(style.marginTop ?? baseMargin);
  const right = toNumberLength(style.marginRight ?? baseMargin);
  const bottom = toNumberLength(style.marginBottom ?? baseMargin);
  const left = toNumberLength(style.marginLeft ?? baseMargin);
  return {
    top,
    right,
    bottom,
    left,
    horizontal: left + right,
    vertical: top + bottom,
  };
};

const areDisplayedRowsEquivalent = (
  left: DisplayedRowEntry[],
  right: DisplayedRowEntry[],
  rowKeyPath: string | undefined,
): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const leftEntry = left[index];
    const rightEntry = right[index];
    if (!leftEntry || !rightEntry) return false;
    const leftKey = deriveStableKey(
      leftEntry.item,
      rowKeyPath,
      leftEntry.originalIndex,
    );
    const rightKey = deriveStableKey(
      rightEntry.item,
      rowKeyPath,
      rightEntry.originalIndex,
    );
    if (leftKey !== rightKey) return false;
    if (Boolean(leftEntry.isExiting) !== Boolean(rightEntry.isExiting)) {
      return false;
    }
    if (Boolean(leftEntry.isAdded) !== Boolean(rightEntry.isAdded)) {
      return false;
    }
    if (!Object.is(leftEntry.item, rightEntry.item)) {
      return false;
    }
  }
  return true;
};

const LayoutListPreview: React.FC<LayoutListPreviewProps> = ({
  node,
  nodeRef,
  nodeWithBehaviorProps,
  listProps,
  rowTemplate,
  data,
  scale,
  renderedX,
  renderedY,
  renderedZIndex,
  resolvedStyle,
  inFlow,
  clipRect,
  renderNode,
  disableBehaviors,
  sceneCatalog,
  componentBindingPatch,
}) => {
  // -----------------------------------------------------------------------
  // 1. Resolve rows (filter, sort, variant)
  // -----------------------------------------------------------------------
  const behaviorFilter = (
    nodeWithBehaviorProps.props as { listFilterPredicate?: unknown } | undefined
  )?.listFilterPredicate;

  const behaviorSortComparator = (
    nodeWithBehaviorProps.props as { listSortComparator?: unknown } | undefined
  )?.listSortComparator;
  const behaviorListSource = (
    nodeWithBehaviorProps.props as { listSource?: unknown } | undefined
  )?.listSource;
  const behaviorRowVariants = (
    nodeWithBehaviorProps.props as { rowVariantRules?: unknown } | undefined
  )?.rowVariantRules as LayoutListRowVariantRuntime[] | undefined;
  const maxItems =
    typeof listProps.maxItems === "number" &&
    Number.isFinite(listProps.maxItems)
      ? Math.max(1, Math.round(listProps.maxItems))
      : DEFAULT_LAYOUT_LIST_MAX_ITEMS;
  const listDirection = listProps.direction ?? DEFAULT_LAYOUT_LIST_DIRECTION;
  const listData = useMemo(() => {
    if (Array.isArray(behaviorListSource)) {
      return behaviorListSource;
    }
    return nodeWithBehaviorProps.dataBinding
      ? resolveBinding(nodeWithBehaviorProps.dataBinding, data)
      : [];
  }, [behaviorListSource, data, nodeWithBehaviorProps.dataBinding]);
  const indexedListData = useMemo(
    () =>
      (Array.isArray(listData) ? listData : []).map((item, rowIndex) => ({
        item,
        originalIndex: rowIndex,
      })),
    [listData],
  );
  const filteredIndexedListData = useMemo(
    () =>
      indexedListData.filter(({ originalIndex }) => {
        if (typeof behaviorFilter !== "function") return true;
        try {
          return Boolean(
            (behaviorFilter as LayoutListFilterPredicate)(
              nodeWithBehaviorProps.dataBinding as DataBinding,
              originalIndex,
              Array.isArray(listData) ? listData : [],
            ),
          );
        } catch {
          return false;
        }
      }),
    [
      behaviorFilter,
      indexedListData,
      listData,
      nodeWithBehaviorProps.dataBinding,
    ],
  );

  const sortedIndexedListData = useMemo(() => {
    const sorted = [...filteredIndexedListData].sort((a, b) => {
      if (typeof behaviorSortComparator !== "function") {
        return a.originalIndex - b.originalIndex;
      }
      try {
        const compared = (behaviorSortComparator as LayoutListSortComparator)(
          nodeWithBehaviorProps.dataBinding as DataBinding,
          a.originalIndex,
          b.originalIndex,
          data,
          Array.isArray(listData) ? listData : [],
        );
        const normalizedCompared = Number.isFinite(compared)
          ? compared
          : a.originalIndex - b.originalIndex;
        if (normalizedCompared !== 0) {
          return normalizedCompared;
        }
        return a.originalIndex - b.originalIndex;
      } catch {
        return a.originalIndex - b.originalIndex;
      }
    });
    return sorted;
  }, [
    behaviorSortComparator,
    data,
    filteredIndexedListData,
    listData,
    nodeWithBehaviorProps.dataBinding,
  ]);
  const previousSortedIndexedListDataRef = useRef<DisplayedRowEntry[]>([]);
  const sortedIndexedListDataStable = useMemo(() => {
    const previous = previousSortedIndexedListDataRef.current;
    if (
      previous.length === sortedIndexedListData.length &&
      sortedIndexedListData.every(
        (entry, index) =>
          previous[index]?.item === entry.item &&
          previous[index]?.originalIndex === entry.originalIndex,
      )
    ) {
      return previous;
    }
    previousSortedIndexedListDataRef.current = sortedIndexedListData;
    return sortedIndexedListData;
  }, [sortedIndexedListData]);
  const previousRowsToRenderRef = useRef<DisplayedRowEntry[]>([]);
  const prevRowMapRef = useRef<Map<string, RowSnapshot>>(new Map());
  const rowKeyPath = listProps.rowKeyPath;
  const rowsToRender: DisplayedRowEntry[] = useMemo(
    () => sortedIndexedListDataStable.slice(0, maxItems),
    [maxItems, sortedIndexedListDataStable],
  );

  const [displayedRowsToRender, setDisplayedRowsToRender] =
    useState(rowsToRender);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const exitTemplateByKeyRef = useRef<Map<string, ContainerNode>>(new Map());

  const isFirstRenderRef = useRef(true);

  useLayoutEffect(() => {
    const previous = previousRowsToRenderRef.current;
    const nextRows = rowsToRender;

    const toKeySet = (rows: DisplayedRowEntry[]) =>
      new Set(
        rows.map((entry) =>
          deriveStableKey(entry.item, rowKeyPath, entry.originalIndex),
        ),
      );

    const previousKeys = toKeySet(previous);
    const nextKeys = toKeySet(nextRows);
    const removedKeys = [...previousKeys].filter((key) => !nextKeys.has(key));
    const addedKeys = [...nextKeys].filter((key) => !previousKeys.has(key));
    const addedKeysSet = new Set(addedKeys);
    const next = nextRows.map((entry) => {
      const key = deriveStableKey(entry.item, rowKeyPath, entry.originalIndex);
      return {
        ...entry,
        isAdded: addedKeysSet.has(key),
        isExiting: removedKeys.includes(key),
      };
    });
    const hasRemoved = removedKeys.length > 0;
    const hasAdded = [...nextKeys].some((key) => !previousKeys.has(key));
    const hasChanges = hasRemoved || hasAdded;

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }

    if (!hasChanges) {
      setDisplayedRowsToRender((currentRows) =>
        areDisplayedRowsEquivalent(currentRows, next, rowKeyPath)
          ? currentRows
          : next,
      );
      previousRowsToRenderRef.current = next;
      return;
    }

    const previousRowMap = prevRowMapRef.current;
    const removedRowsExitWaitMs = disableBehaviors
      ? 0
      : removedKeys.reduce((maxWaitMs, key) => {
          const removedRow = previousRowMap.get(key);
          const rowExitWaitMs = Math.max(
            0,
            estimateListExitWaitMsFromBehavior(
              removedRow?.row.template.behavior,
            ),
          );
          return Math.max(maxWaitMs, rowExitWaitMs);
        }, 0);
    const previousRowsExitWaitMs = disableBehaviors
      ? 0
      : [...previousRowMap.values()].reduce((maxWaitMs, row) => {
          const rowExitWaitMs = Math.max(
            0,
            estimateListExitWaitMsFromBehavior(row.row.template.behavior),
          );
          return Math.max(maxWaitMs, rowExitWaitMs);
        }, 0);
    const isGrowingListWithoutRemovedRows = hasAdded && !hasRemoved;
    const exitWaitMs = isGrowingListWithoutRemovedRows
      ? previousRowsExitWaitMs
      : removedRowsExitWaitMs;

    if (exitWaitMs <= 0) {
      for (const key of removedKeys) {
        exitTemplateByKeyRef.current.delete(key);
      }
      setDisplayedRowsToRender((currentRows) =>
        areDisplayedRowsEquivalent(currentRows, next, rowKeyPath)
          ? currentRows
          : next,
      );
      previousRowsToRenderRef.current = next;
      return;
    }

    if (hasRemoved) {
      const removedKeySet = new Set(removedKeys);
      for (const key of removedKeys) {
        const cachedTemplate = exitTemplateByKeyRef.current.get(key);
        const removedSnapshot = previousRowMap.get(key);
        if (cachedTemplate) {
          exitTemplateByKeyRef.current.set(key, cachedTemplate);
        } else if (removedSnapshot?.row.template) {
          exitTemplateByKeyRef.current.set(key, removedSnapshot.row.template);
        }
      }
      const rowsWithExitState = previous.map((entry) => {
        const key = deriveStableKey(
          entry.item,
          rowKeyPath,
          entry.originalIndex,
        );
        return {
          ...entry,
          isExiting: removedKeySet.has(key),
        };
      });
      setDisplayedRowsToRender((currentRows) =>
        areDisplayedRowsEquivalent(currentRows, rowsWithExitState, rowKeyPath)
          ? currentRows
          : rowsWithExitState,
      );
    }

    transitionTimeoutRef.current = setTimeout(() => {
      for (const key of removedKeys) {
        exitTemplateByKeyRef.current.delete(key);
      }
      setDisplayedRowsToRender((currentRows) =>
        areRowsEquivalentByKey(currentRows, next, rowKeyPath)
          ? currentRows
          : next,
      );
      previousRowsToRenderRef.current = next;
      transitionTimeoutRef.current = null;
    }, exitWaitMs);
  }, [disableBehaviors, rowsToRender, rowKeyPath]);

  useEffect(
    () => () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    },
    [],
  );

  const sourceKey = getDataBindingSource(nodeWithBehaviorProps.dataBinding);
  const rowBindingExpressions = useMemo(
    () =>
      collectLayoutListRowBindingExpressions(
        rowTemplate,
        listProps.rowVariants,
        sceneCatalog,
      ),
    [listProps.rowVariants, rowTemplate, sceneCatalog],
  );
  const rowVariantConditionExpressions = useMemo(() => {
    const rules = componentBindingPatch?.rowVariantBindingRules;
    if (!rules?.length) return [] as BindingExpression[];
    return rules
      .map((rule) => rule.conditionExpression)
      .filter((expression): expression is BindingExpression =>
        Boolean(expression),
      );
  }, [componentBindingPatch]);
  const usedExternalSources = useMemo(
    () =>
      collectRowTemplateUsedExternalSources(
        rowTemplate,
        listProps.rowVariants,
        sceneCatalog,
        sourceKey,
        [...rowBindingExpressions, ...rowVariantConditionExpressions],
      ),
    [
      listProps.rowVariants,
      rowBindingExpressions,
      rowTemplate,
      rowVariantConditionExpressions,
      sceneCatalog,
      sourceKey,
    ],
  );
  const prevInjectedDataRef = useRef<InjectedData | undefined>(undefined);
  const prevRowFingerprintsRef = useRef<Map<string, RowBindingFingerprint>>(
    new Map(),
  );
  const prevRowItemsRef = useRef<Map<string, unknown>>(new Map());
  const useFrozenDataForRowSnapshots = useMemo(
    () => shouldUseFrozenData(rowTemplate, listProps.rowVariants),
    [rowTemplate, listProps.rowVariants],
  );
  const normalizedBehaviorRowVariants = useMemo(
    () =>
      (behaviorRowVariants ?? [])
        .filter(
          (
            variant,
          ): variant is LayoutListRowVariantRuntime & {
            name: string;
            conditionEvaluator: LayoutListRowVariantRuntime["conditionEvaluator"];
          } =>
            typeof variant?.name === "string" &&
            variant.name.trim().length > 0 &&
            typeof variant.conditionEvaluator === "function",
        )
        .map((variant, index) => ({
          ...variant,
          order:
            typeof variant.order === "number" && Number.isFinite(variant.order)
              ? variant.order
              : index,
          name: variant.name.trim(),
        }))
        .sort((a, b) => a.order - b.order),
    [behaviorRowVariants],
  );

  const variantTemplateByName = useMemo(
    () =>
      new Map(
        (listProps.rowVariants ?? []).map((variant) => [
          variant.name,
          variant.template,
        ]),
      ),
    [listProps.rowVariants],
  );

  const nextRowsKeySet = useMemo(
    () =>
      new Set(
        rowsToRender.map((entry) =>
          deriveStableKey(entry.item, rowKeyPath, entry.originalIndex),
        ),
      ),
    [rowsToRender, rowKeyPath],
  );

  const displayedRowsForRender = useMemo(() => {
    return displayedRowsToRender.map((entry) => {
      const key = deriveStableKey(entry.item, rowKeyPath, entry.originalIndex);
      const isPendingRemoval = !nextRowsKeySet.has(key);

      return {
        ...entry,
        isExiting: Boolean(entry.isExiting) || isPendingRemoval,
        isAdded: isPendingRemoval ? false : Boolean(entry.isAdded),
      };
    });
  }, [displayedRowsToRender, nextRowsKeySet, rowKeyPath]);
  const previousRowsListDataRef = useRef<unknown[]>([]);
  const rowsListData = useMemo(() => {
    const next = displayedRowsForRender.map((entry) => entry.item);
    const previous = previousRowsListDataRef.current;
    if (
      previous.length === next.length &&
      next.every((item, index) => Object.is(previous[index], item))
    ) {
      return previous;
    }
    previousRowsListDataRef.current = next;
    return next;
  }, [displayedRowsForRender]);

  type RowLayoutCacheEntry = {
    item: unknown;
    template: ContainerNode;
    outerHeight: number;
    outerWidth: number;
    matchedVariantName: string | null;
    bindingFingerprintKey: string;
  };
  const rowLayoutCacheRef = useRef<Map<string, RowLayoutCacheEntry>>(new Map());
  const layoutListDataRef = useRef(data);
  layoutListDataRef.current = data;

  const rowFingerprintByKey = useMemo(() => {
    const layoutData = data;
    const previousData = prevInjectedDataRef.current;
    const previousFingerprints = prevRowFingerprintsRef.current;
    const dataBinding = nodeWithBehaviorProps.dataBinding as
      | DataBinding
      | undefined;

    const activeKeys = new Set(
      displayedRowsForRender.map((entry) =>
        deriveStableKey(entry.item, rowKeyPath, entry.originalIndex),
      ),
    );

    const pruneFingerprintMap = (
      fingerprints: Map<string, RowBindingFingerprint>,
    ): Map<string, RowBindingFingerprint> => {
      for (const key of fingerprints.keys()) {
        if (!activeKeys.has(key)) {
          fingerprints.delete(key);
        }
      }
      return fingerprints;
    };

    const computeFingerprintForRow = (
      entry: DisplayedRowEntry,
      visibleIndex: number,
    ): RowBindingFingerprint => {
      const scopedRowData = buildLayoutListScopedRowData(layoutData, {
        rowIndex: visibleIndex,
        rowItem: entry.item,
        listSourceKey: sourceKey,
        rowsListData,
      });
      return computeRowBindingFingerprint(
        rowBindingExpressions,
        scopedRowData,
        rowVariantConditionExpressions,
        () => {
          const matched = resolveMatchedRowVariant(
            normalizedBehaviorRowVariants,
            dataBinding,
            scopedRowData,
          );
          return matched?.name ?? null;
        },
      );
    };

    const canReusePreviousFingerprints =
      previousData != null &&
      !hasInjectableSourcesChanged(
        previousData,
        layoutData,
        usedExternalSources,
      );

    if (canReusePreviousFingerprints) {
      const fingerprints = new Map(previousFingerprints);
      displayedRowsForRender.forEach((entry, visibleIndex) => {
        const key = deriveStableKey(
          entry.item,
          rowKeyPath,
          entry.originalIndex,
        );
        const previousItem = prevRowItemsRef.current.get(key);
        if (previousItem !== undefined && Object.is(previousItem, entry.item)) {
          return;
        }
        fingerprints.set(key, computeFingerprintForRow(entry, visibleIndex));
      });
      return pruneFingerprintMap(fingerprints);
    }

    const fingerprints = new Map<string, RowBindingFingerprint>();

    displayedRowsForRender.forEach((entry, visibleIndex) => {
      const key = deriveStableKey(entry.item, rowKeyPath, entry.originalIndex);
      fingerprints.set(key, computeFingerprintForRow(entry, visibleIndex));
    });

    return fingerprints;
  }, [
    data,
    displayedRowsForRender,
    normalizedBehaviorRowVariants,
    nodeWithBehaviorProps.dataBinding,
    rowBindingExpressions,
    rowKeyPath,
    rowVariantConditionExpressions,
    rowsListData,
    sourceKey,
    usedExternalSources,
  ]);

  useLayoutEffect(() => {
    prevInjectedDataRef.current = data;
    prevRowFingerprintsRef.current = rowFingerprintByKey;
    const nextItems = new Map<string, unknown>();
    displayedRowsForRender.forEach((entry) => {
      nextItems.set(
        deriveStableKey(entry.item, rowKeyPath, entry.originalIndex),
        entry.item,
      );
    });
    prevRowItemsRef.current = nextItems;
  }, [data, displayedRowsForRender, rowFingerprintByKey, rowKeyPath]);

  const resolvedRows: ResolvedRow[] = useMemo(() => {
    const layoutData = layoutListDataRef.current;
    const rows = displayedRowsForRender.map((entry, visibleIndex) => {
      const key = deriveStableKey(entry.item, rowKeyPath, entry.originalIndex);
      const previousSnapshot = prevRowMapRef.current.get(key);
      const frozenExitTemplate = exitTemplateByKeyRef.current.get(key);
      const isPendingRemoval = !nextRowsKeySet.has(key);
      const shouldFreezeTemplate = entry.isExiting || isPendingRemoval;
      const rowFingerprint = rowFingerprintByKey.get(key);
      const bindingFingerprintKey = rowFingerprint
        ? serializeRowBindingFingerprint(rowFingerprint)
        : "";
      const cachedLayout = rowLayoutCacheRef.current.get(key);
      if (
        cachedLayout &&
        Object.is(cachedLayout.item, entry.item) &&
        cachedLayout.bindingFingerprintKey === bindingFingerprintKey &&
        !shouldFreezeTemplate &&
        !entry.isExiting
      ) {
        return {
          key,
          originalIndex: entry.originalIndex,
          visibleIndex,
          item: entry.item,
          isExiting: Boolean(entry.isExiting),
          isAdded: Boolean(entry.isAdded),
          template: cachedLayout.template,
          offsetX: 0,
          offsetY: 0,
          outerHeight: cachedLayout.outerHeight,
          outerWidth: cachedLayout.outerWidth,
        };
      }

      if (entry.isExiting && previousSnapshot) {
        return {
          key,
          originalIndex: previousSnapshot.row.originalIndex,
          visibleIndex: previousSnapshot.row.visibleIndex,
          item: previousSnapshot.row.item,
          isExiting: true,
          isAdded: false,
          template: frozenExitTemplate ?? previousSnapshot.row.template,
          offsetX: previousSnapshot.row.offsetX,
          offsetY: previousSnapshot.row.offsetY,
          outerHeight: previousSnapshot.row.outerHeight,
          outerWidth: previousSnapshot.row.outerWidth,
        };
      }
      const frozenRowData =
        useFrozenDataForRowSnapshots && previousSnapshot?.frozenData
          ? previousSnapshot.frozenData
          : undefined;
      const scopedRowData: InjectedData = {
        ...(frozenRowData ?? layoutData),
        rowIndex: visibleIndex,
        layoutListRowItem: entry.item,
      };
      if (sourceKey) {
        (scopedRowData as unknown as Record<string, unknown>)[sourceKey] =
          frozenRowData
            ? (frozenRowData as unknown as Record<string, unknown>)[sourceKey]
            : (rowsListData as unknown);
      }
      const matchedVariant = resolveMatchedRowVariant(
        normalizedBehaviorRowVariants,
        nodeWithBehaviorProps.dataBinding as DataBinding | undefined,
        scopedRowData,
      );
      const matchedVariantName = matchedVariant?.name ?? null;
      const resolvedTemplate =
        (matchedVariant
          ? variantTemplateByName.get(matchedVariant.name)
          : undefined) ?? rowTemplate;

      const templateForRow = shouldFreezeTemplate
        ? (frozenExitTemplate ??
          previousSnapshot?.row.template ??
          resolvedTemplate)
        : resolvedTemplate;
      if (!shouldFreezeTemplate && !exitTemplateByKeyRef.current.has(key)) {
        exitTemplateByKeyRef.current.set(key, templateForRow);
      }
      const templateResolvedStyle = resolveStyles(
        templateForRow.style,
        scopedRowData,
      );
      const templateMargins = getMarginInsetsFromStyle(templateResolvedStyle);
      const outerHeight = Math.max(
        0,
        (Number.isFinite(templateForRow.height) ? templateForRow.height : 0) +
          templateMargins.vertical,
      );
      const outerWidth = Math.max(
        0,
        (Number.isFinite(templateForRow.width) ? templateForRow.width : 0) +
          templateMargins.horizontal,
      );
      if (!shouldFreezeTemplate && !entry.isExiting) {
        rowLayoutCacheRef.current.set(key, {
          item: entry.item,
          template: templateForRow,
          outerHeight,
          outerWidth,
          matchedVariantName,
          bindingFingerprintKey,
        });
      }
      return {
        key,
        originalIndex: entry.originalIndex,
        visibleIndex,
        item: entry.item,
        isExiting: Boolean(entry.isExiting),
        template: templateForRow,
        isAdded: Boolean(entry.isAdded),
        offsetX: 0,
        offsetY: 0,
        outerHeight,
        outerWidth,
      };
    });

    const activeRows = rows.filter((row) => !row.isExiting);
    const positionedActive = resolveLayoutListRowOffsets(
      activeRows,
      listDirection,
    );
    let activeIndex = 0;
    return rows.map((row) => {
      if (row.isExiting) {
        return row;
      }
      const positioned = positionedActive[activeIndex];
      activeIndex += 1;
      return positioned ?? row;
    });
  }, [
    displayedRowsForRender,
    listDirection,
    normalizedBehaviorRowVariants,
    nodeWithBehaviorProps.dataBinding,
    rowFingerprintByKey,
    variantTemplateByName,
    rowTemplate,
    rowKeyPath,
    rowsListData,
    sourceKey,
    nextRowsKeySet,
    useFrozenDataForRowSnapshots,
  ]);

  useEffect(() => {
    for (const key of rowLayoutCacheRef.current.keys()) {
      if (!nextRowsKeySet.has(key)) {
        rowLayoutCacheRef.current.delete(key);
      }
    }
  }, [nextRowsKeySet]);

  // -----------------------------------------------------------------------
  // 2. Transition engine: diff keys, emit enter signals
  // -----------------------------------------------------------------------
  const buildRowData = useCallback(
    (
      visibleIndex: number,
      rowItem: unknown,
      signal?: "onListEnter" | "onListExit",
    ): InjectedData => {
      const rowData: InjectedData = {
        ...data,
        rowIndex: visibleIndex,
        listTransitionSignal: signal,
        layoutListRowItem: rowItem,
      };
      return rowData;
    },
    [data],
  );

  // After render: persist current resolved rows for next diff.
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
    // Persist current resolved rows for next diff
    const previousMap = prevRowMapRef.current;
    const nextMap = new Map<string, RowSnapshot>();
    for (const row of resolvedRows) {
      const previousSnapshot = previousMap.get(row.key);
      const isPendingRemoval = !nextRowsKeySet.has(row.key);
      const shouldKeepFrozenData =
        row.isExiting &&
        previousSnapshot != null &&
        previousSnapshot.row.isExiting;
      const shouldKeepPreviousRowSnapshot =
        previousSnapshot != null && (row.isExiting || isPendingRemoval);
      nextMap.set(row.key, {
        row: shouldKeepPreviousRowSnapshot ? previousSnapshot.row : row,
        frozenData: shouldKeepFrozenData
          ? previousSnapshot.frozenData
          : buildRowData(row.visibleIndex, row.item),
      });
    }
    prevRowMapRef.current = nextMap;
  }, [resolvedRows, buildRowData, nextRowsKeySet]);

  // -----------------------------------------------------------------------
  // 3b. FLIP technique: fix transitions for elements moved down by React
  // -----------------------------------------------------------------------
  // React's list reconciliation physically moves DOM nodes for items that
  // shift to a later position (removeChild + insertBefore). This resets the
  // browser's knowledge of the previous `transform` value, so CSS transitions
  // don't fire. We fix this by snapping moved elements back to their old
  // position in useLayoutEffect (before paint), then letting CSS transition
  // them to the new position.
  const prevOffsetsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );
  const rowElsRef = useRef<Map<string, HTMLDivElement | null>>(new Map());

  useLayoutEffect(() => {
    const prev = prevOffsetsRef.current;
    const els = rowElsRef.current;
    const movedEls: {
      el: HTMLDivElement;
      newOffsetX: number;
      newOffsetY: number;
    }[] = [];

    for (const row of resolvedRows) {
      const prevOffset = prev.get(row.key);
      const el = els.get(row.key);
      if (
        el &&
        prevOffset != null &&
        (prevOffset.x !== row.offsetX || prevOffset.y !== row.offsetY)
      ) {
        movedEls.push({
          el,
          newOffsetX: row.offsetX,
          newOffsetY: row.offsetY,
        });
        el.style.transform = `translate(${prevOffset.x * scale}px, ${prevOffset.y * scale}px)`;
      }
    }

    if (movedEls.length > 0) {
      void movedEls[0]!.el.offsetHeight;
      for (const { el, newOffsetX, newOffsetY } of movedEls) {
        el.style.transform = `translate(${newOffsetX * scale}px, ${newOffsetY * scale}px)`;
      }
    }

    const next = new Map<string, { x: number; y: number }>();
    for (const row of resolvedRows) {
      next.set(row.key, { x: row.offsetX, y: row.offsetY });
    }
    prevOffsetsRef.current = next;
  }, [resolvedRows, scale]);

  // -----------------------------------------------------------------------
  // 4. Render
  // -----------------------------------------------------------------------

  const previewContentHeight = useMemo(() => {
    const rowSpan = isLayoutListDirectionHorizontal(listDirection)
      ? resolvedRows.reduce(
          (maxHeight, row) => Math.max(maxHeight, row.outerHeight),
          0,
        )
      : resolvedRows.reduce((total, row) => total + row.outerHeight, 0);
    return rowSpan + getMarginInsetsFromStyle(node.style ?? {}).vertical;
  }, [listDirection, resolvedRows, node.style]);

  const previewContentWidth = useMemo(() => {
    const rowSpan = isLayoutListDirectionHorizontal(listDirection)
      ? resolvedRows.reduce((total, row) => total + row.outerWidth, 0)
      : resolvedRows.reduce(
          (maxWidth, row) => Math.max(maxWidth, row.outerWidth),
          0,
        );
    return rowSpan + getMarginInsetsFromStyle(node.style ?? {}).horizontal;
  }, [listDirection, resolvedRows, node.style]);

  const renderedClipPath = useMemo(
    () =>
      inFlow
        ? undefined
        : buildLayoutListRenderedClipPath(
            clipRect,
            renderedX,
            renderedY,
            previewContentWidth,
            previewContentHeight,
            scale,
            node,
          ),
    [
      clipRect,
      inFlow,
      node,
      previewContentHeight,
      previewContentWidth,
      renderedX,
      renderedY,
      scale,
    ],
  );

  const renderNodeRef = useRef(renderNode);
  renderNodeRef.current = renderNode;
  const renderNodeStable = useCallback<LayoutListProps["renderNode"]>(
    (params) => renderNodeRef.current(params),
    [],
  );

  const registerRowElement = useCallback(
    (key: string, element: HTMLDivElement | null) => {
      if (element) {
        rowElsRef.current.set(key, element);
        return;
      }
      rowElsRef.current.delete(key);
    },
    [],
  );

  const rowRenderEntries = useMemo(
    () =>
      resolvedRows.map((row, renderIndex) => {
        const signal = row.isExiting
          ? ("onListExit" as const)
          : row.isAdded
            ? ("onListEnter" as const)
            : undefined;
        const previousSnapshot = prevRowMapRef.current.get(row.key);
        const rowData: InjectedData =
          row.isExiting && previousSnapshot
            ? {
                ...previousSnapshot.frozenData,
                listTransitionSignal: "onListExit" as const,
                rowIndex: row.visibleIndex,
                layoutListRowItem: row.item,
              }
            : buildRowData(row.visibleIndex, row.item, signal);
        const rowTemplateNode: ContainerNode = {
          ...row.template,
          kind: "container",
          id: `${row.template.id}::row-${row.key}`,
          zIndex: row.template.zIndex + renderIndex * 1000,
        };
        return {
          row,
          rowData,
          rowTemplateNode,
          bindingFingerprint: rowFingerprintByKey.get(row.key),
        };
      }),
    [buildRowData, resolvedRows, rowFingerprintByKey],
  );

  return (
    <Box
      key={`${node.id}::layer`}
      sx={{
        position: node.style?.position === "relative" ? "relative" : "absolute",
        inset: 0,
        zIndex: renderedZIndex,
      }}
    >
      <Box
        key={`${node.id}`}
        ref={nodeRef}
        sx={{
          position:
            node.style?.position === "relative" ? "relative" : "absolute",
          left: renderedX * scale,
          top: renderedY * scale,
          width: previewContentWidth * scale,
          height: previewContentHeight * scale,
          zIndex: 0,
          backgroundColor: resolvedStyle.backgroundColor as string | undefined,
          ...getResolvedBorderSx(resolvedStyle),
          ...getResolvedSpacingSx(resolvedStyle, scale, {
            includeMargin: false,
          }),
          transform: composeNodeTransform(node.style?.transform, node.rotation),
          transformOrigin: "center center",
          boxSizing: "border-box",
          clipPath: renderedClipPath,
          WebkitClipPath: renderedClipPath,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Active rows - memoized per row to limit subtree re-renders */}
        {rowRenderEntries.map(
          ({ row, rowData, rowTemplateNode, bindingFingerprint }) => (
            <LayoutListPreviewRow
              key={`row-${row.key}`}
              row={row}
              rowData={rowData}
              rowTemplateNode={rowTemplateNode}
              scale={scale}
              listDirection={listDirection}
              previewContentWidth={previewContentWidth}
              previewContentHeight={previewContentHeight}
              rowKeyPath={rowKeyPath}
              listSourceKey={sourceKey}
              bindingFingerprint={bindingFingerprint}
              renderNode={renderNodeStable}
              registerRowElement={registerRowElement}
            />
          ),
        )}
      </Box>
    </Box>
  );
};

export default LayoutList;
